import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import axiosInstance, { axiosSagaRequest } from "clients/api";
import { implementPromiseAction } from "@adobe/redux-saga-promise";
import { setError } from "../error/slice";
import dayjs from "dayjs";
import {
  ADD_ITEM_FROM_WIZARD,
  callAddDocumentWizardDefinitionPromise,
  CHANGE_ITEM_FROM_WIZARD,
  CREATE_DOCUMENT_FROM_WIZARD,
  DOCUMENT_WIZARD_ADD_CALL,
  DOCUMENT_WIZARD_CALL,
  DOCUMENT_WIZARD_DEFINITION_CALL,
  DOCUMENT_WIZARD_DEFINITION_COPY_CALL,
  DOCUMENT_WIZARD_DEFINITION_DELETE_CALL,
  DOCUMENT_WIZARD_DEFINITION_EDIT_CALL,
  DOCUMENT_WIZARD_DEFINITION_RESTORE_CALL,
  DOCUMENT_WIZARD_DEFINITIONS_CALL,
  DOCUMENT_WIZARD_DEFINITIONS_CALL_CONDITIONS,
  DOCUMENT_WIZARD_DELETE_CALL,
  DOCUMENT_WIZARD_EDIT_CALL,
  DOCUMENT_WIZARDS_CALL
} from "./sagas-actions";
import {
  onCallDocumentWizardDefinitionConditions,
  onCallDocumentWizardDefinitions,
  onCallDocumentWizards,
  onFailedDocumentWizard,
  onFailedDocumentWizardDefinition,
  onFailedDocumentWizardDefinitionConditions,
  onFailedDocumentWizardDefinitions,
  onFailedDocumentWizards,
  onSuccessDocumentWizard,
  onSuccessDocumentWizardAdd,
  onSuccessDocumentWizardDefinition,
  onSuccessDocumentWizardDefinitionAdd,
  onSuccessDocumentWizardDefinitionConditions,
  onSuccessDocumentWizardDefinitionDelete,
  onSuccessDocumentWizardDefinitionEdit,
  onSuccessDocumentWizardDefinitions,
  onSuccessDocumentWizardDelete,
  onSuccessDocumentWizards,
  switchDocumentWizardDefinitionDeleteLoading,
  switchDocumentWizardDefinitionLoading,
  switchDocumentWizardLoading
} from "./slice";
import { v4 as uuidv4 } from "uuid";

import { onSuccessRestoreItems, switchDeletedItemsLoading } from "../deleted-items/slice";
import { onSuccessDocumentItem } from "../documents/slice";
import { calculateProductServices, calculateSubTotal } from "../../utils/calculations";
import { getMaxProductServicePosition } from "../../utils/document/productServices";
import {
  applyBundleChildRemovals,
  buildTreeDeletionMap,
  isMarkedForDeleteInWizard,
  mergeItemsToAddWithBundles,
  mergeFlatBundleChildrenIntoParents,
  stripDeletedFromItemsToAdd,
  collectNewlyAddedProductServiceIds,
  collectProductServiceDeleteIds,
  resolveItemsToDeleteForApi
} from "../../utils/document-wizard/bundleChildRemoval";
import { AZURE_STORAGE_PREFIX } from "../../constant/env-variables";
import { userDataSelector } from "../user/selectors";
import { documentDataSelector } from "../documents/selectors";
import { cloneDeep } from "lodash";
import generateNewIds, { getDuplicateProductServicesMedia } from "../../utils/document/productServices";
import { prepareProductServiceTreeForApiUpsert } from "../../utils/document/productServicePhotos";
import { evaluateFormula, extractFieldReferences } from "../../utils/calculated-fields/formulaParser";
import {
  applyProductServiceFieldUpdateToTree,
  applyQuantityChangesToTree,
  findProductServiceInTree,
  parseProductServiceReference,
  recalculateProductServiceTotalsInTree,
  resolveProductServiceTargetId
} from "../../utils/document-wizard/changeDocumentValue";

const prefix = "/tenantUser/documents/document_wizard";
const definitionsPrefix = "/tenantUser/documents/document_wizard_definition";

const normalizeTokenToColumn = (token) =>
  String(token || "")
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

const resolveDocumentFieldValue = (tokenName, mergeFields, docData, documentRecord) => {
  const documentMergeField = (mergeFields || []).find((field) =>
    field?.group_name === "Document" && String(field?.token || "") === String(tokenName || "")
  );
  const columnName = String(documentMergeField?.column_name || "").trim();
  const normalizedColumnName = normalizeTokenToColumn(tokenName);

  return docData?.[columnName]
    ?? documentRecord?.[columnName]
    ?? docData?.[normalizedColumnName]
    ?? documentRecord?.[normalizedColumnName]
    ?? 0;
};

// Initial call
function* documentWizardDefinitionsCallGenerator(action) {
  yield put(onCallDocumentWizardDefinitions());
  try {
    const { data } = yield call(axiosSagaRequest, "get", definitionsPrefix);
    yield put(onSuccessDocumentWizardDefinitions(data.documentWizards));
  } catch (error) {
    yield put(
      setError({
        error,
        action
      })
    );
    yield put(onFailedDocumentWizardDefinitions());
  }
}

// Single Document Wizard Definition
function* documentWizardDefinitionCallGenerator(action) {
  yield put(switchDocumentWizardDefinitionLoading(true));
  try {
    const {
      payload: { documentWizardDefinitionId }
    } = action;
    const { data } = yield call(axiosSagaRequest, "get", `${definitionsPrefix}/${documentWizardDefinitionId}`);

    const result = { ...data };

    if (Array.isArray(data?.data)) {
      result.settingPresets = {};
      result.formulas = [];
    } else {
      result.data = typeof data?.data?.data === "string" ? JSON.parse(data?.data?.data) : [];
      result.settingPresets = data?.data?.settings_presets;
      result.formulas = Array.isArray(data?.data?.formulas) ? data.data.formulas : [];
    }
    yield put(onSuccessDocumentWizardDefinition(result));
  } catch (error) {
    yield put(
      setError({
        error,
        action
      })
    );
    yield put(onFailedDocumentWizardDefinition());
  } finally {
    yield put(switchDocumentWizardDefinitionLoading(false));
  }
}

function* documentWizardDefinitionAddCallPromiseGenerator(action) {
  yield call(implementPromiseAction, action, function* documentWizardDefinitionAddWorker() {
    try {
      yield put(switchDocumentWizardDefinitionLoading(true));
      const { payload } = action;
      const formData = new FormData();

      console.log(payload, "payload inside create");
      payload.image &&
        typeof payload.image === "object" &&
        payload.image.src.file &&
        formData.append("image", payload.image.src.file);
      formData.append(
        "data",
        JSON.stringify({
          data: payload.data,
          settings_presets: payload.settingPresets || {},
          formulas: payload.formulas || []
        })
      );
      formData.append("description", payload.description);
      formData.append("name", payload.name);
      formData.append("status_id", payload.status_id);

      // add document wizard definition
      const { data } = yield call(axiosSagaRequest, "post", definitionsPrefix, formData);

      yield payload.message && payload.message();
      yield put(onSuccessDocumentWizardDefinitionAdd(data));

      return data;
    } catch (error) {
      yield put(
        setError({
          error,
          action
        })
      );
    } finally {
      yield put(switchDocumentWizardDefinitionLoading(false));
    }
  });
}

function* documentWizardDefinitionEditCallGenerator(action) {
  yield put(switchDocumentWizardDefinitionLoading(true));
  try {
    const {
      payload: { documentWizardDefinitionData, messageFunction }
    } = action;
    const formData = new FormData();

    formData.append(
      "data",
      JSON.stringify({
        data: documentWizardDefinitionData.data,
        settings_presets: documentWizardDefinitionData.settingPresets || {},
        formulas: documentWizardDefinitionData.formulas || []
      })
    );
    formData.append("description", documentWizardDefinitionData.description);
    formData.append("name", documentWizardDefinitionData.name);
    formData.append("status_id", documentWizardDefinitionData.status_id);

    documentWizardDefinitionData.image &&
      typeof documentWizardDefinitionData.image === "object" &&
      documentWizardDefinitionData.image.src?.file &&
      formData.append("image", documentWizardDefinitionData.image.src.file);

    if (documentWizardDefinitionData.image && documentWizardDefinitionData.image.cleared) {
      formData.append("image", "");
    }

    // update document wizard definition data
    const { data } = yield call(
      axiosSagaRequest,
      "put",
      `${definitionsPrefix}/${documentWizardDefinitionData.document_wizard_definition_id}`,
      formData
    );

    console.log(data, 'are we here ????');
    yield messageFunction && messageFunction();
    yield put(onSuccessDocumentWizardDefinitionEdit(data));
  } catch (error) {
    yield put(
      setError({
        error,
        action
      })
    );
  }
  yield put(switchDocumentWizardDefinitionLoading(false));
}

function* documentWizardDefinitionDeleteCallGenerator(action) {
  yield put(switchDocumentWizardDefinitionDeleteLoading(true));
  try {
    const {
      payload: { documentWizardDefinitionId }
    } = action;
    yield call(axiosSagaRequest, "delete", `${definitionsPrefix}/${documentWizardDefinitionId}`);
    yield put(onSuccessDocumentWizardDefinitionDelete(documentWizardDefinitionId));
  } catch (error) {
    yield put(
      setError({
        error,
        action
      })
    );
  }
  yield put(switchDocumentWizardDefinitionDeleteLoading(false));
}

function* documentWizardDefinitionRestoreCallGenerator(action) {
  yield put(switchDeletedItemsLoading(true));
  try {
    const {
      payload: { ids }
    } = action;
    yield call(axiosSagaRequest, "post", `${definitionsPrefix}/undo_deleted`, {
      document_wizard_definition_ids: ids
    });
    yield put(onSuccessRestoreItems(ids));
  } catch (error) {
    yield put(
      setError({
        error,
        action
      })
    );
  }
  yield put(switchDeletedItemsLoading(false));
}

function selectDocumentWizardId({ payload }) {
  const { documentWizardId } = payload;
  return documentWizardId;
}

function* documentWizardDefinitionConditionsCallGenerator(action) {
  yield put(onCallDocumentWizardDefinitionConditions());
  try {
    const { data } = yield call(axiosSagaRequest, "get", `${prefix}/tokens/get_conditions`);
    yield put(onSuccessDocumentWizardDefinitionConditions(data));
  } catch (error) {
    yield put(
      setError({
        error,
        action
      })
    );
    yield put(onFailedDocumentWizardDefinitionConditions());
  }
}

function* documentWizardsCallGenerator(action) {
  yield put(onCallDocumentWizards());
  try {
    const { data } = yield call(axiosSagaRequest, "get", prefix);
    yield put(onSuccessDocumentWizards(data.documentWizards));
  } catch (error) {
    yield put(
      setError({
        error,
        action
      })
    );
    yield put(onFailedDocumentWizards());
  }
}

// Single Document Wizard
function* documentWizardCallGenerator(action) {
  try {
    const {
      payload: { documentWizardId }
    } = action;

    const { data } = yield call(axiosSagaRequest, "get", `${prefix}/${documentWizardId}`);

    yield put(onSuccessDocumentWizard(data));
  } catch (error) {
    yield put(
      setError({
        error,
        action
      })
    );
    yield put(onFailedDocumentWizard());
  }
}

function* documentWizardAddCallGenerator(action) {
  yield put(switchDocumentWizardLoading(true));
  try {
    const {
      payload: { wizardData, messageFunction }
    } = action;
    const { data } = yield call(axiosInstance.post, prefix, wizardData);
    yield put(onSuccessDocumentWizardAdd(data));
    yield messageFunction && messageFunction();
  } catch (error) {
    yield put(
      setError({
        error,
        action
      })
    );
  }
  yield put(switchDocumentWizardLoading(false));
}

function* documentWizardEditCallGenerator(action) {
  yield put(switchDocumentWizardLoading(true));
  try {
    const {
      payload: { documentWizardId, documentWizardData, messageFunction }
    } = action;

    // update document wizard data
    yield call(axiosSagaRequest, "put", `${prefix}/${documentWizardId}`, documentWizardData);
    yield messageFunction && messageFunction();
  } catch (error) {
    yield put(
      setError({
        error,
        action
      })
    );
  }
  yield put(switchDocumentWizardLoading(false));
}

function* documentWizardDeleteCallGenerator(action) {
  try {
    const {
      payload: { documentWizardId }
    } = action;
    yield call(axiosSagaRequest, "delete", `${prefix}/${documentWizardId}`);
    yield put(onSuccessDocumentWizardDelete(documentWizardId));
  } catch (error) {
    yield put(
      setError({
        error,
        action
      })
    );
  }
}

/** Wizard RHF payloads should be { code, name } but some paths/store shapes only supply legacy keys; normalize before saga touches .code / .name. */
function normalizeWizardContactRef(ref) {
  if (ref == null || ref === "") {
    return ref;
  }
  if (typeof ref === "object") {
    const code = ref.code ?? ref.contact_id ?? ref.id ?? ref.UUID;
    if (code != null && String(code) !== "") {
      return {
        ...ref,
        code,
        name: ref.name ?? ref.label ?? ref.contact_name ?? ref.full_name ?? ""
      };
    }
  }
  const code = String(ref).trim();
  return code ? { code, name: "" } : ref;
}

function* createDocumentFromWizardGenerator({ payload }) {
  const { data, resolve, reject, generateProductServiceUrl } = payload;
  const dataNorm = {
    ...data,
    bill_to_contact_id: normalizeWizardContactRef(data.bill_to_contact_id),
    ship_to_contact_id: normalizeWizardContactRef(data.ship_to_contact_id),
    sold_to_contact_id: normalizeWizardContactRef(data.sold_to_contact_id)
  };

  try {
    const createDocData = {
      bill_to_contact_id: dataNorm.bill_to_contact_id.code,
      ship_to_contact_id: dataNorm.ship_to_contact_id.code,
      sold_to_contact_id: dataNorm.sold_to_contact_id.code,
      tenant_user_id: dataNorm.tenant_user_id,
      read_only: false
    };
    const contactEnum = {
      0: "bill_to",
      1: "sold_to",
      2: "ship_to"
    };

    const AddressEnum = {
      bill_to: "Default Billing",
      sold_to: "Default Main",
      ship_to: "Default Shipping"
    };

    const contacts = [
      dataNorm.bill_to_contact_id,
      dataNorm.sold_to_contact_id,
      dataNorm.ship_to_contact_id
    ].reduce(
      (acc, current, index) => {
        const foundItem = acc.find((item) => item.code === current.code);
        if (!foundItem) {
          acc.push({ ...current, for: [index] });
          return acc;
        }
        if (foundItem) {
          return acc.map((item) => {
            if (item.code === current.code) {
              return { ...item, for: [...item.for, index] };
            }
            return item;
          });
        }
        return acc;
      },
      []
    );

    let companyId;

    yield all(
      contacts.map(async (contact) => {
        const { data } = await axiosSagaRequest("get", `/tenantUser/contacts/${contact.code}`);
        companyId = data?.contact[0]?.company_id?.company_id;
        if (companyId) {
          contact.for.map((f) => {
            createDocData[`${contactEnum[f]}_company_id`] = companyId;
          });
          const { data: companyAddress } = await axiosSagaRequest(
            "get",
            `/tenantUser/companies/company_address/${companyId}/get_company_addresses`
          );
          companyAddress.clearedTableData.map((address) => {
            contact.for.map((f) => {
              if (address[AddressEnum[contactEnum[f]]]) {
                createDocData[`${contactEnum[f]}_company_address_id`] = address.company_address_id || "";
              }
            });
          });
        }
      })
    );

    const { data: documentTemplateData } = yield axiosSagaRequest(
      "get",
      `/tenantUser/documents/${dataNorm.document_id}`
    );

    createDocData.adjustments = documentTemplateData.document.adjustments;
    createDocData.tenant_user_id = documentTemplateData.document.tenant_user_id;
    createDocData.cost = documentTemplateData.document.cost;
    createDocData.currency_id = documentTemplateData.document.currency_id;
    createDocData.document_date = dayjs().toISOString();

    const tenantSettings = yield select((state) => state.documentData.tenantSettings?.data);
    const expireDays = tenantSettings?.document_expire_day;
    createDocData.expire_date = expireDays != null
      ? dayjs().add(expireDays, "day").toISOString()
      : documentTemplateData.document.expire_date;
    createDocData.payment_term_id = documentTemplateData.document.payment_term_id;
    createDocData.sales_tax_id = documentTemplateData.document.sales_tax_id;
    createDocData.sales_tax_rate = documentTemplateData.document.sales_tax_rate;
    createDocData.status_id = documentTemplateData.document.status_id;
    createDocData.sub_total = documentTemplateData.document.sub_total;
    createDocData.type_id = documentTemplateData.document.type_id;
    createDocData.opportunity_add_edit = documentTemplateData.document.opportunity_add_edit;

    if (documentTemplateData.document.opportunity_add_edit) {
      const { oppStatus, oppBoard, oppType, oppStage } = yield all({
        oppStatus: call(axiosSagaRequest, "get", "tenantUser/opportunity-setting/list/opportunity_status"),
        oppBoard: call(axiosSagaRequest, "get", "tenantUser/opportunity-setting/list/opportunity_board"),
        oppType: call(axiosSagaRequest, "get", "tenantUser/opportunity-setting/list/opportunity_type"),
        oppStage: call(axiosSagaRequest, "get", "tenantUser/opportunity-setting/list/opportunity_stage")
      });

      const defaultStatus = oppStatus.data.tableData.find((item) => item.default_value);
      const defaultBoard = oppBoard.data.tableData.find((item) => item.default_value);
      const defaultType = oppType.data.tableData.find((item) => item.default_value);
      const defaultStage = oppStage.data.tableData.find((item) => item.default_value);

      const oppData = {
        board_id: defaultBoard.opportunity_board_id,
        company_id: companyId || null,
        contact_id: dataNorm.sold_to_contact_id.code,
        currency_id: documentTemplateData.document.currency_id,
        name: `${documentTemplateData.document.document_name} - ${String(Date.now())}`,
        stage_id: defaultStage.opportunity_stage_id,
        status_id: defaultStatus.opportunity_status_id,
        type_id: defaultType.opportunity_type_id,
        tenant_user_id: dataNorm.tenant_user_id
      };

      const { data: responseOppCreate } = yield axiosSagaRequest("post", "/tenantUser/opportunities", oppData);
      createDocData.opportunity_id = responseOppCreate?.createdOpportunity?.opportunity_id || "";
    }

    createDocData.name = `${documentTemplateData.document.document_name} for ${dataNorm.sold_to_contact_id.name}`;

    const { data: documentTemplateItems } = yield axiosSagaRequest(
      "get",
      `/tenantUser/documents/items/${documentTemplateData.document.document_id}`
    );

    const res = yield axiosSagaRequest("post", "/tenantUser/documents", createDocData);

    const originalTemplateItems = cloneDeep(documentTemplateItems);

    const productServicesWithNewIds = [];

    const referenceProductServiceId = {};
    const referenceLayoutItemId = {};

    const { user } = yield select(userDataSelector);
    const tenant_id = user?.tenant_id;

    const currentDocId = res?.data?.document?.document_id;

    const newLayout = documentTemplateItems.layoutData.layout.map((item) => {
      const newItemId = uuidv4();
      if (item.typeId === "product_service") {
        documentTemplateItems.productServices.map((productService) => {
          if (productService.document_item_id === item.i) {
            const { newPs, childrenIdReference, oldPs } = generateNewIds(productService, newItemId);
            referenceProductServiceId[productService.document_item_product_service_id] =
              newPs.document_item_product_service_id;
            Object.entries(childrenIdReference || {}).forEach(([templateChildId, documentChildId]) => {
              referenceProductServiceId[templateChildId] = documentChildId;
            });
            if (!newPs.vendor_id) {
              delete newPs.vendor_name;
            }
            productServicesWithNewIds.push({ newPs, childrenIdReference, oldPs });
          }
        });
      }
      documentTemplateItems.layoutData.items[newItemId] = documentTemplateItems.layoutData.items[item.i];
      referenceLayoutItemId[item.i] = newItemId;
      delete documentTemplateItems.layoutData.items[item.i];
      return {
        ...item,
        i: newItemId
      };
    });

    const productServiceMediaToDuplicate = getDuplicateProductServicesMedia(
      productServicesWithNewIds,
      generateProductServiceUrl,
      tenant_id
    );

    if (currentDocId) {
      const graphicItems = documentTemplateItems.layoutData.layout.filter((l) => l.typeId === "graphic");
      const videoItems = documentTemplateItems.layoutData.layout.filter((l) => l.typeId === "video");

      const graphicMedias = graphicItems.map((item) => {
        const itemValue = originalTemplateItems.layoutData.items[item.i];
        const fromAddress = `${AZURE_STORAGE_PREFIX}/${tenant_id}/documents/${documentTemplateData?.document.document_id}/${itemValue}`;
        const toAddress = `${AZURE_STORAGE_PREFIX}/${tenant_id}/documents/${currentDocId}/${itemValue}`;
        return {
          from: fromAddress,
          to: toAddress
        };
      });

      const videoMedias = videoItems.flatMap((item) => {
        const itemValue = originalTemplateItems.layoutData.items[item.i];
        const mediaKeys = [itemValue];

        if (typeof itemValue === "string") {
          try {
            const parsedValue = JSON.parse(itemValue);
            if (parsedValue && typeof parsedValue === "object") {
              mediaKeys.length = 0;
              if (parsedValue.video) mediaKeys.push(parsedValue.video);
              if (parsedValue.thumbnail) mediaKeys.push(parsedValue.thumbnail);
            }
          } catch (e) {
            // Keep original value when itemValue is a plain UUID/string.
          }
        }

        return mediaKeys
          .filter(Boolean)
          .map((mediaKey) => ({
            from: `${AZURE_STORAGE_PREFIX}/${tenant_id}/documents/${documentTemplateData?.document.document_id}/${mediaKey}`,
            to: `${AZURE_STORAGE_PREFIX}/${tenant_id}/documents/${currentDocId}/${mediaKey}`
          }));
      });

      const newMedias = [...graphicMedias, ...videoMedias];

      const media = [
        ...(newMedias.length ? newMedias : []),
        ...(productServiceMediaToDuplicate?.length ? productServiceMediaToDuplicate : [])
      ];

      yield call(axiosInstance.post, "/base/duplicate_media", media);
    }

    const updateItems = {
      documentId: res.data.document.document_id,
      docData: {
        sub_total: Number(documentTemplateData.document.sub_total),
        sales_tax_rate: documentTemplateData.document.sales_tax_rate,
        cost: Number(documentTemplateData.document.cost)
      },
      layoutData: JSON.stringify({
        ...documentTemplateItems.layoutData,
        layout: newLayout,
        deletedItemIds: [],
        itemUrls: []
      }),
      productServiceData: JSON.stringify({
        delete: [],
        upsert: prepareProductServiceTreeForApiUpsert(productServicesWithNewIds.map((item) => item.newPs))
      })
    };

    const createdDocItems = yield axiosSagaRequest("post", "/tenantUser/documents/items", updateItems);
    resolve({
      document: res.data.document,
      items: createdDocItems.data,
      updateItems,
      referenceProductServiceId,
      referenceLayoutItemId
    });
  } catch (e) {
    yield put(
      setError({
        e
      })
    );
    reject(e);
  }
}

function parseLayoutDataForWizardChange(data) {
  const raw = data?.updateItems?.layoutData ?? data?.documentItems?.layoutData;
  if (raw == null) return { layout: [], items: {} };
  const obj =
    typeof raw === "string"
      ? (() => {
          try {
            return JSON.parse(raw);
          } catch {
            return {};
          }
        })()
      : raw;
  return {
    layout: obj?.layout || [],
    items: obj?.items || {}
  };
}

function addProductServiceTreeDescendantsToDeleteMap(ps, mergedItemsToDelete) {
  if (!ps || !Array.isArray(ps.children) || !ps.children.length) return;
  ps.children.forEach((child) => {
    const cid = child?.document_item_product_service_id;
    if (cid != null) mergedItemsToDelete[cid] = true;
    addProductServiceTreeDescendantsToDeleteMap(child, mergedItemsToDelete);
  });
}

function expandBundleDeletionTargets(productServices, mergedItemsToDelete) {
  const list = productServices || [];
  list.forEach((ps) => {
    if (ps?.document_item_product_service_id != null && mergedItemsToDelete[ps.document_item_product_service_id]) {
      addProductServiceTreeDescendantsToDeleteMap(ps, mergedItemsToDelete);
    }
  });
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < list.length; i += 1) {
      const ps = list[i];
      const id = ps?.document_item_product_service_id;
      if (id != null && !mergedItemsToDelete[id]) {
        const parentRef = ps?.product_service_parent_id?.[0];
        if (parentRef != null && mergedItemsToDelete[parentRef]) {
          mergedItemsToDelete[id] = true;
          changed = true;
          addProductServiceTreeDescendantsToDeleteMap(ps, mergedItemsToDelete);
        }
      }
    }
  }
}

function* changeItemFromWizard({ payload }) {
  const { data, resolve, reject } = payload;

  try {
    const { layout: layoutLayout, items: finalItems } = parseLayoutDataForWizardChange(data);
    const productServiceBlockIds = new Set(
      (layoutLayout || [])
        .filter((item) => item.typeId === "product_service")
        .map((item) => String(item.i))
    );

    const itemsToAdd = stripDeletedFromItemsToAdd(
      data.itemsToAdd || [],
      buildTreeDeletionMap(data.itemsToDelete || {}, data.referenceProductServiceId || {})
    );
    const newlyAddedIds = collectNewlyAddedProductServiceIds(itemsToAdd);
    const treeDeletionMap = buildTreeDeletionMap(
      data.itemsToDelete || {},
      data.referenceProductServiceId || {}
    );
    const mergedItemsToDelete = resolveItemsToDeleteForApi(
      { ...(data.itemsToDelete || {}) },
      data.referenceProductServiceId || {},
      newlyAddedIds
    );
    (data.documentItems?.productServices || []).forEach((ps) => {
      const psId = ps?.document_item_product_service_id;
      const blockId = ps?.document_item_id;
      if (psId == null || blockId == null) return;
      if (!productServiceBlockIds.has(String(blockId))) {
        mergedItemsToDelete[psId] = true;
      }
    });

    expandBundleDeletionTargets(data.documentItems?.productServices, mergedItemsToDelete);

    const productServicesAfterRemovals = mergeFlatBundleChildrenIntoParents(
      applyBundleChildRemovals(data.documentItems?.productServices || [], treeDeletionMap)
    );

    let newUpsert = productServicesAfterRemovals;

    newUpsert = newUpsert.filter(
      (ps) => !isMarkedForDeleteInWizard(treeDeletionMap, ps.document_item_product_service_id)
    );

    const newDelete = collectProductServiceDeleteIds(
      data.documentItems?.productServices || [],
      mergedItemsToDelete
    );

    if (itemsToAdd?.length) {
      const batchCountByBlock = {};
      const itemsWithPositions = itemsToAdd.map((ps) => {
        const blockKey = String(ps.document_item_id ?? "");
        const blockItems = newUpsert.filter(
          (item) => !item.is_inside_bundle && String(item.document_item_id ?? "") === blockKey
        );
        const batchIndex = batchCountByBlock[blockKey] ?? 0;
        batchCountByBlock[blockKey] = batchIndex + 1;
        const highestPosition = getMaxProductServicePosition(blockItems);

        return {
          ...ps,
          position: highestPosition + batchIndex + 1
        };
      });

      newUpsert = mergeItemsToAddWithBundles(
        newUpsert,
        itemsWithPositions,
        data.referenceProductServiceId || {}
      );
    }

    if (data.itemsToChangeQuantity && Object.keys(data.itemsToChangeQuantity).length) {
      newUpsert = applyQuantityChangesToTree(newUpsert, data.itemsToChangeQuantity);
    }

    const currentProductServices = productServicesAfterRemovals.reduce((acc, current) => {
      if (!isMarkedForDeleteInWizard(treeDeletionMap, current?.document_item_product_service_id)) {
        acc.push(current);
        return acc;
      }
      return acc;
    }, []);

    let finalProductService = [...newUpsert, ...currentProductServices].reduce((acc, current) => {
      const foundItem = acc.find(
        (item) => item.document_item_product_service_id === current.document_item_product_service_id
      );
      if (!foundItem) {
        acc.push({
          ...current,
          total: Number(current.sell || 0) * Number(current.quantity || 1) - Number(current.discount || 0)
        });
        return acc;
      }
      return acc;
    }, []);

    const productServiceFieldUpdates = Array.isArray(data?.productServiceFieldUpdates)
      ? data.productServiceFieldUpdates
      : [];
    if (productServiceFieldUpdates.length) {
      const addItemActionProductServiceMap = data?.addItemActionProductServiceMap || {};
      const documentMergeFields = Array.isArray(data?.documentMergeFields) ? data.documentMergeFields : [];
      const docDataValues = data?.updateItems?.docData || {};
      const documentRecord = data?.document || {};
      const editableFields = new Set(["sell", "quantity", "msrp", "map", "cost", "discount"]);

      productServiceFieldUpdates.forEach((update) => {
        const targetField = String(update?.targetField || "");
        if (!editableFields.has(targetField)) return;

        const targetProductServiceId =
          update?.targetProductServiceId ||
          resolveProductServiceTargetId(
            update?.productServiceRef,
            data.referenceProductServiceId || {},
            addItemActionProductServiceMap
          );
        if (!targetProductServiceId) return;

        const formula = update?.formula;
        if (!formula?.formula) return;

        const currentPs = findProductServiceInTree(finalProductService, targetProductServiceId);
        if (!currentPs) return;
        const sell = Number(currentPs?.sell) || 0;
        const quantity = Number(currentPs?.quantity) || 0;
        const msrp = Number(currentPs?.msrp) || 0;
        const map = Number(currentPs?.map) || 0;
        const cost = Number(currentPs?.cost) || 0;
        const discount = Number(currentPs?.discount) || 0;
        const valuesMap = {
          "Product Service.Sell": sell,
          "ProductService.Sell": sell,
          "Product Service.Quantity": quantity,
          "ProductService.Quantity": quantity,
          "Product Service.MSRP": msrp,
          "ProductService.MSRP": msrp,
          "Product Service.MAP": map,
          "ProductService.MAP": map,
          "Product Service.Cost": cost,
          "ProductService.Cost": cost,
          "Product Service.Discount": discount,
          "ProductService.Discount": discount
        };

        const formulaFieldRefs = extractFieldReferences(formula.formula);
        formulaFieldRefs.forEach((ref) => {
          const fieldKey = String(ref || "").replace(/^\[/, "").replace(/\]$/, "");
          if (!fieldKey.startsWith("Document.")) return;
          const tokenName = fieldKey.slice("Document.".length);
          const rawValue = resolveDocumentFieldValue(tokenName, documentMergeFields, docDataValues, documentRecord);
          const numericValue = Number(rawValue);
          valuesMap[fieldKey] = Number.isFinite(numericValue) ? numericValue : 0;
        });

        let nextValue = 0;
        try {
          nextValue = evaluateFormula(formula.formula, valuesMap);
        } catch (e) {
          return;
        }

        finalProductService = applyProductServiceFieldUpdateToTree(
          finalProductService,
          targetProductServiceId,
          targetField,
          Number(nextValue) || 0
        );
      });
    }

    finalProductService = recalculateProductServiceTotalsInTree(finalProductService);

    finalProductService = finalProductService.filter(
      (ps) => ps?.document_item_id != null && productServiceBlockIds.has(String(ps.document_item_id))
    );

    const productServiceItems = (layoutLayout || []).filter((item) => item.typeId === "product_service") || [];

    const documentSubTotal = productServiceItems?.reduce((acc, current) => {
      const topLevelBundleGroup = finalItems?.[current.i] ? JSON.parse(finalItems?.[current.i]).bundle_group : {};
      const psItemProductServices = finalProductService?.filter(
        (ps) => String(ps.document_item_id) === String(current.i)
      );
      const subTotal = calculateSubTotal(
        calculateProductServices(psItemProductServices, "document_item_product_service_id"),
        topLevelBundleGroup
      );
      acc += Number(subTotal) || 0;
      return acc;
    }, 0);

    const upsertForApi = calculateProductServices(
      prepareProductServiceTreeForApiUpsert(finalProductService),
      "document_item_product_service_id"
    );

    const finalData = {
      ...data.updateItems,
      docData: { ...data.updateItems?.docData, sub_total: documentSubTotal },
      productServiceData: JSON.stringify({ delete: newDelete || [], upsert: upsertForApi || [] })
    };
    const createdDocItems = yield axiosSagaRequest("post", "/tenantUser/documents/items", finalData);
    yield put(
      onSuccessDocumentItem({
        itemData: createdDocItems.data
      })
    );
    resolve(createdDocItems);
  } catch (e) {
    reject(e);
  }
}

function* AddItemFromWizard({ payload }) {
  const { data, resolve, reject } = payload;
  try {
    const finalData = {
      ...data.updateItems,
      productServiceData: JSON.stringify({
        delete: [],
        upsert: prepareProductServiceTreeForApiUpsert(data.productServices || [])
      })
    };
    const createdDocItems = yield axiosSagaRequest("post", "/tenantUser/documents/items", finalData);
    resolve(createdDocItems);
  } catch (e) {
    reject(e);
  }
}

function* copyDocumentWizardGenerator(action) {
  try {
    const { payload } = action;
    yield put(switchDocumentWizardDefinitionLoading(true));
    const formData = new FormData();

    payload.image &&
      typeof payload.image === "object" &&
      payload.image.src?.file &&
      formData.append("image", payload.image.src.file);
    if (payload.image && payload.image.cleared) {
      formData.append("image", "");
    }
    formData.append("document_wizard_definition_id", payload.document_wizard_definition_id);
    formData.append("description", payload.description);
    formData.append("name", payload.name);

    // add document wizard definition
    const { data } = yield call(axiosSagaRequest, "post", `${definitionsPrefix}/copy`, formData);

    yield payload.message && payload.message();
    yield put(onSuccessDocumentWizardDefinitionAdd(data));

    return data;
  } catch (error) {
    yield put(
      setError({
        error,
        action
      })
    );
  } finally {
    yield put(switchDocumentWizardDefinitionLoading(false));
  }
}

export default function* documentWizardsSaga() {
  yield takeEvery(DOCUMENT_WIZARD_DEFINITIONS_CALL, documentWizardDefinitionsCallGenerator);
  yield takeEvery(DOCUMENT_WIZARD_DEFINITION_CALL, documentWizardDefinitionCallGenerator);
  yield takeEvery(callAddDocumentWizardDefinitionPromise, documentWizardDefinitionAddCallPromiseGenerator);
  yield takeEvery(DOCUMENT_WIZARD_DEFINITION_EDIT_CALL, documentWizardDefinitionEditCallGenerator);
  yield takeEvery(DOCUMENT_WIZARD_DEFINITION_DELETE_CALL, documentWizardDefinitionDeleteCallGenerator);
  yield takeEvery(DOCUMENT_WIZARD_DEFINITION_RESTORE_CALL, documentWizardDefinitionRestoreCallGenerator);

  yield takeEvery(DOCUMENT_WIZARDS_CALL, documentWizardsCallGenerator);
  yield takeEvery(DOCUMENT_WIZARD_CALL, documentWizardCallGenerator);
  yield takeEvery(DOCUMENT_WIZARD_ADD_CALL, documentWizardAddCallGenerator);
  yield takeEvery(DOCUMENT_WIZARD_EDIT_CALL, documentWizardEditCallGenerator);
  yield takeEvery(DOCUMENT_WIZARD_DELETE_CALL, documentWizardDeleteCallGenerator);
  yield takeEvery(CREATE_DOCUMENT_FROM_WIZARD, createDocumentFromWizardGenerator);
  yield takeEvery(CHANGE_ITEM_FROM_WIZARD, changeItemFromWizard);
  yield takeEvery(ADD_ITEM_FROM_WIZARD, AddItemFromWizard);
  yield takeEvery(DOCUMENT_WIZARD_DEFINITION_COPY_CALL, copyDocumentWizardGenerator);

  // yield takeEvery(DOCUMENT_WIZARD_RESTORE_CALL, documentWizardDeleteCallGenerator);

  yield takeLatest(DOCUMENT_WIZARD_DEFINITIONS_CALL_CONDITIONS, documentWizardDefinitionConditionsCallGenerator);
}
